import * as React from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import InfoIcon from '@mui/icons-material/Info';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { REMINDER, HOME, LOGO} from '../../assets/Icons'
import SendIcon from '@mui/icons-material/Send';
import { OverridableStringUnion } from "@mui/types";
import { ButtonPropsVariantOverrides } from "@mui/material/Button";
import { useNavigate } from 'react-router-dom'

const drawerWidth = 240;

enum Position {
  Top,
  Bottom
}

enum AuthRequirement {
  Authenticated,
  UnauthenticatedOnly,
  NoAuthentication,
}

export interface NavItem {
  title: string
  icon: any,
  position: Position
  handler: any
  auth: AuthRequirement
  variant: OverridableStringUnion<"text" | "outlined" | "contained", ButtonPropsVariantOverrides>
}

export default function AppMenu() {
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { isAuthenticated, logout, loginWithRedirect } = useAuth0();

  // const navigate = (location: string) => {
  //   window.location.href = location;
  // };
  
  const handleLogout = () => {
    window.sessionStorage.clear();
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };
  
  const navItems: NavItem[] = [{
    title: 'Home', 
    icon: HOME(24),
    position: Position.Top,
    handler: () => {navigate("/");},
    auth: AuthRequirement.NoAuthentication,
    variant: "text"
  },{
    title: 'Collections', 
    icon: REMINDER(24),
    position: Position.Top,
    handler: () => {navigate("/collections");},
    auth: AuthRequirement.Authenticated,
    variant: "text"
  },
  {
    title: 'Notifications', 
    icon: <SendIcon/>,
    position: Position.Top,
    handler: () => navigate("/notifications"),
    auth: AuthRequirement.Authenticated,
    variant: "text"
  },
  {
    title: 'Contact', 
    icon: <MailOutlineIcon/>,
    position: Position.Bottom,
    handler: () => {navigate("/contact");handleDrawerToggle()},
    auth: AuthRequirement.NoAuthentication,
    variant: "text"
  },{
    title: 'About', 
    icon: <InfoIcon/>,
    position: Position.Bottom,
    handler: () => {navigate("/about");handleDrawerToggle()},
    auth: AuthRequirement.NoAuthentication,
    variant: "text"
  },{
    title: 'Logout', 
    icon: <LogoutIcon/>,
    position: Position.Bottom,
    handler: () => handleLogout(),
    auth: AuthRequirement.Authenticated,
    variant: "text"
  },{
    title: 'Login', 
    icon: <LoginIcon/>,
    position: Position.Bottom,
    handler: () => loginWithRedirect(),
    auth: AuthRequirement.UnauthenticatedOnly,
    variant: "text"
  },{
    title: 'Sign Up', 
    icon: <AccountCircleIcon/>,
    position: Position.Top,
    handler: () => loginWithRedirect({authorizationParams: { screen_hint: "signup" }}),
    auth: AuthRequirement.UnauthenticatedOnly,
    variant: "outlined"
  }];

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };


  const navAuthFilter = (item: NavItem): boolean => 
    (item.auth === AuthRequirement.NoAuthentication) ||
    (item.auth === AuthRequirement.Authenticated && isAuthenticated) ||
    (item.auth === AuthRequirement.UnauthenticatedOnly && !isAuthenticated);

  const posFilter = (item: NavItem, position: Position): boolean => 
    (item.position === position) 

  const drawer = (
    <Box onClick={handleDrawerToggle}>
      <List>
        {navItems
          .filter(item => navAuthFilter(item))
          .filter(item => item.position === Position.Top)
          .map((item, index) => (
          <ListItem disablePadding key={index}>
            <Link onClick={item.handler} style={{width: "100%"}}>
              <ListItemButton>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText secondary={item.title}/>
              </ListItemButton>
            </Link>
          </ListItem>
        ))}
      </List>
      <List style={{ position: "absolute", bottom: "0", width: "100%" }}>
        {navItems
          .filter(item => navAuthFilter(item))
          .filter(item => posFilter(item, Position.Bottom))
          .map((item, index) => (
          <ListItem disablePadding key={index}>
            <Link onClick={() => {
                handleDrawerToggle()
                item.handler()
              }} style={{width: "100%"}}>
              <ListItemButton>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText secondary={item.title}/>
              </ListItemButton>
            </Link>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar component="nav" className="toolbar-background">
        <Toolbar sx={{ pl: 1 }}>
          <span style={{flexGrow: 1, fontFamily: "Montserrat", marginTop: "7px"}} aria-label="Collecting logo" role="img">{LOGO}</span>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' } }}>
            <MenuIcon aria-hidden="true" />
          </IconButton>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            {navItems
              .filter(item => navAuthFilter(item))
              .filter(item => posFilter(item, Position.Bottom) || posFilter(item, Position.Top))
              .map((item) => (
              <Button color="inherit" key={item.title} onClick={() => item.handler()} variant={item.variant}>
                {item.title}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          anchor={"right"}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}>
          {drawer}
        </Drawer>
      </nav>
      <Box component="main" sx={{ p: 1 }}>
        <Toolbar />
      </Box>
    </Box>
  );
}